/**
 * Horizontal guided-step indicator for Cycle Builder.
 */
import React from "react";
import { translateUi } from '../i18n/index';
import { CheckCircle2 } from "../icons";
import { cb, cbCn } from "./cycleBuilderTheme";

export type GuideStepId = "method" | "pickTemplate" | "details" | "criteria" | "events";

export const GUIDE_STEPS: { id: GuideStepId; label: string; short: string }[] = [
  { id: "method", label: "How to build", short: "Method" },
  { id: "pickTemplate", label: "Select template", short: "Template" },
  { id: "details", label: "Cycle details", short: "Details" },
  { id: "criteria", label: "Review criteria", short: "Criteria" },
  { id: "events", label: "Assessment events", short: "Events" },
];

export function WizardGuideBar({
  active,
  completedIds,
  onJump,
  isDarkMode = false,
  isMobile = false,
  hiddenIds = [],
}: {
  active: GuideStepId;
  /** Steps the user may jump back to (already visited / completed). */
  completedIds: GuideStepId[];
  onJump?: (id: GuideStepId) => void;
  isDarkMode?: boolean;
  isMobile?: boolean;
  hiddenIds?: GuideStepId[];
}) {
  const steps = GUIDE_STEPS.filter((s) => !hiddenIds.includes(s.id));
  const activeIndex = steps.findIndex((s) => s.id === active);

  return (
    <nav aria-label={translateUi("Cycle builder progress")} className={cb.guideTrack(isDarkMode)}>
      <ol className={cbCn("flex w-full", isMobile ? "flex-col gap-2" : "items-center gap-0")}>
        {steps.map((step, index) => {
          const isActive = step.id === active;
          const visited = completedIds.includes(step.id);
          const done = visited && !isActive && index < activeIndex;
          const state: "active" | "done" | "idle" = isActive ? "active" : done || (visited && !isActive) ? "done" : "idle";
          const jumpable = Boolean(onJump && visited && !isActive);

          return (
            <li
              key={step.id}
              className={cbCn(
                "flex min-w-0",
                isMobile ? "w-full" : "flex-1 items-center",
                !isMobile && index < steps.length - 1 && "min-w-0"
              )}
            >
              <button
                type="button"
                disabled={!jumpable}
                onClick={() => jumpable && onJump?.(step.id)}
                className={cbCn(
                  "flex min-w-0 items-center gap-2.5 rounded-xl px-2 py-2 text-left transition-colors",
                  isMobile ? "w-full min-h-[44px]" : "w-full",
                  jumpable && (isDarkMode ? "hover:bg-slate-800/80" : "hover:bg-white/70"),
                  !jumpable && "cursor-default"
                )}
                aria-current={isActive ? "step" : undefined}
              >
                <span className={cb.guideDot(isDarkMode, state)}>
                  {done ? <CheckCircle2 className="h-4 w-4" aria-hidden /> : <span>{index + 1}</span>}
                </span>
                <span className="min-w-0">
                  <span
                    className={cbCn(
                      "block truncate text-sm font-semibold",
                      isActive
                        ? isDarkMode
                          ? "text-indigo-100"
                          : "text-indigo-900"
                        : done
                          ? isDarkMode
                            ? "text-emerald-200"
                            : "text-emerald-800"
                          : isDarkMode
                            ? "text-slate-400"
                            : "text-slate-500"
                    )}
                  >
                    {isMobile ? translateUi(step.label) : translateUi(step.short)}
                  </span>
                  {!isMobile && (
                    <span className={cbCn("block truncate text-[11px]", cb.bodySm(isDarkMode))}>{translateUi(step.label)}</span>
                  )}
                </span>
              </button>
              {!isMobile && index < steps.length - 1 && (
                <span
                  className={cbCn(
                    "mx-1 h-px w-full max-w-[2.5rem] shrink grow",
                    index < activeIndex
                      ? isDarkMode
                        ? "bg-emerald-500/50"
                        : "bg-emerald-300"
                      : isDarkMode
                        ? "bg-slate-600"
                        : "bg-slate-200"
                  )}
                  aria-hidden
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
